import { combineReducers } from 'redux'
import vehiculoReducer, {
  initialState as vehiculoInitialState,
  SearchNeumaticosVehiculoStateType,
} from './IdentificadorVehiculo/Infrastructure/store/vehiculoSlice'
import medidasReducer, {
  initialState as medidasInitialState,
  SearchNeumaticosMedidasStateType,
} from './SeleccionMedidas/Infrastructure/store/medidasSlice'
import { IAppState } from '../../../common/context/context'
import tallerReducer, {
  initialState as tallerInitialState,
  SearchNeumaticosTallerStateType,
} from './SeleccionTaller/Infrastructura/store/tallerSlice'
import { NeumaticosFilterValues } from '../catalogo/api/neumaticosFiltro'
import { Temporada } from '../../../common/context/reducers/selectorReducer'
import { createSelector } from '@reduxjs/toolkit'
import { UserState } from '../../../common/context/reducers/userReducer'
import {
  SearchParameters,
  WebType,
} from '../../../common/context/actions/searchActions'
import { Taller } from '../../../common/types/Taller'

/** Codi per `cod_tienda` al WS: almacén Navision (`codigo_tienda`), no el codi de taller. */
function codTiendaForWs(taller: Taller): string | undefined {
  const codigoTienda = taller.codigo_tienda?.trim()
  if (codigoTienda) return codigoTienda
  const codigoTaller = taller.codigo_taller?.trim()
  return codigoTaller || undefined
}

export const version = 1
export const key = 'searchNeumaticos'

export type PageMedida = {
  ancho?: number
  serie?: number
  llanta?: number
}

export interface SearchNeumaticosStateType {
  _version: number
  vehiculo: SearchNeumaticosVehiculoStateType
  medidas: SearchNeumaticosMedidasStateType
  taller: SearchNeumaticosTallerStateType
}

export const initialState: SearchNeumaticosStateType = {
  _version: version,
  vehiculo: vehiculoInitialState,
  medidas: medidasInitialState,
  taller: tallerInitialState,
}

const searchNeumaticosVersionReducer = (
  state: number = version,
  _action: unknown
): number => state

export const searchNeumaticosReducer = combineReducers({
  _version: searchNeumaticosVersionReducer,
  vehiculo: vehiculoReducer,
  medidas: medidasReducer,
  taller: tallerReducer,
})

export default searchNeumaticosReducer

export const selectSearchNeumaticos = (
  state: IAppState
): SearchNeumaticosStateType => (state as IAppState).searchNeumaticos

/** Paràmetres per a l'API legacy `/neumaticos/selector` (cesta / funnel clàssic). */
export const mapSearchNeumaticosToSearchParameters = (
  searchState: SearchNeumaticosStateType,
  userInfo: UserState
): SearchParameters => {
  const { medidas } = searchState
  const { selectedMedidas, selectedEstacion } = medidas

  const query: SearchParameters = {
    pagination: { page: 0, pageSize: 20 },
    order: '',
    direction: 'ASC',
    web: process.env.GATSBY_WEB as WebType,
    filters: {},
    userInfo,
  }

  if (selectedMedidas.ancho) query.filters.ancho = Number(selectedMedidas.ancho)
  if (selectedMedidas.serie) query.filters.serie = Number(selectedMedidas.serie)
  if (selectedMedidas.llanta)
    query.filters.llanta = Number(selectedMedidas.llanta)
  if (selectedMedidas.carga) query.filters.ic = Number(selectedMedidas.carga)
  if (selectedMedidas.velocidad)
    query.filters.cv = String(selectedMedidas.velocidad)

  if (selectedEstacion === 'invierno') query.filters.invierno = true
  if (selectedEstacion === 'allseason') query.filters.allseason = true
  if (selectedEstacion === 'verano') query.filters.verano = true

  const tallerSeleccionado = searchState.taller?.tallerSeleccionado
  if (tallerSeleccionado) {
    const codTienda = codTiendaForWs(tallerSeleccionado)
    if (codTienda) query.filters.cod_tienda = codTienda
  }

  return query
}

export const mapSearchNeumaticosToQuery = (
  searchState: SearchNeumaticosQueryState,
  options: {
    device: 'MOBILE' | 'TABLET' | 'DESKTOP'
    email?: string | null
    phone?: string | null
    zipcode?: string | null
    permisoComercial?: number | null
  }
): NeumaticosFilterValues => {
  const { device, email, phone, zipcode, permisoComercial } = options

  const { medidas, taller } = searchState
  const { selectedMedidas, selectedEstacion, pageMedida } = medidas
  const shouldIncludeWs = Boolean(
    selectedEstacion && selectedEstacion !== 'verano'
  )

  const filters: NeumaticosFilterValues = {
    permiso_comercial: permisoComercial || 0,
    device,
    email,
    phone,
    zipcode,
    origen: shouldIncludeWs ? '/,WS' : '/',
    useragent: typeof navigator !== 'undefined' ? navigator.userAgent : 'SSR',
  }

  if (selectedMedidas.ancho) filters.ancho = Number(selectedMedidas.ancho)
  if (selectedMedidas.serie) filters.serie = Number(selectedMedidas.serie)
  if (selectedMedidas.llanta) filters.llanta = Number(selectedMedidas.llanta)
  if (selectedMedidas.carga) filters.ic = Number(selectedMedidas.carga)
  if (selectedMedidas.velocidad) filters.cv = String(selectedMedidas.velocidad)
  if (selectedEstacion) filters.estacion = [selectedEstacion]

  if (pageMedida) {
     delete filters.ic
     delete filters.cv
  }

  const tallerSeleccionado = taller?.tallerSeleccionado
  if (tallerSeleccionado) {
    const codTienda = codTiendaForWs(tallerSeleccionado)
    if (codTienda) filters.cod_tienda = codTienda
  }

  return filters
}

export type SearchNeumaticosQueryState = {
  vehiculo: SearchNeumaticosStateType['vehiculo']
  taller: SearchNeumaticosStateType['taller']
  medidas: {
    selectedMedidas: SearchNeumaticosStateType['medidas']['selectedMedidas']
    selectedEstacion: Temporada | null
    pageMedida: PageMedida | null
  }
}

export const selectSearchNeumaticosQueryState = createSelector(
  [selectSearchNeumaticos],
  (s): SearchNeumaticosQueryState => ({
    vehiculo: s.vehiculo,
    taller: s.taller,
    medidas: {
      selectedMedidas: s.medidas.selectedMedidas,
      selectedEstacion: s.medidas.selectedEstacion,
      pageMedida: s.medidas.pageMedida,
    },
  })
)
